import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Filters from './Filters';
import Memberships from '../Memberships';
import BoardMembershipPermissionsSelectStep from '../BoardMembershipPermissionsSelectStep';

import styles from './BoardActions.module.scss';

const BoardActions = React.memo(
  ({
    memberships,
    labels,
    filterUsers,
    filterLabels,
    filterText,
    allUsers,
    canEdit,
    canEditMemberships,
    onMembershipCreate,
    onMembershipUpdate,
    onMembershipDelete,
    onUserToFilterAdd,
    onUserFromFilterRemove,
    onLabelToFilterAdd,
    onLabelFromFilterRemove,
    onLabelCreate,
    onLabelUpdate,
    onLabelMove,
    onLabelDelete,
    onTextFilterUpdate,
    // FAMILY-EXT: feature 1
    showHiddenCards,
    boardId,
    onShowHiddenToggle,
    // FAMILY-EXT: фильтр "Только активные"
    showOnlyActive,
    onShowOnlyActiveToggle,
    // FAMILY-EXT: mode = 'all' (default) | 'memberships' | 'filters'
    mode,
    // FAMILY-EXT: компактный inline-режим — для встраивания прямо в шапку
    // (без скролл-обёртки и больших отступов; используется на десктопе).
    inline,
  }) => {
    const showMemberships = mode === 'all' || mode === 'memberships';
    const showFilters = mode === 'all' || mode === 'filters';
    return (
      <div className={classNames(styles.wrapper, inline && styles.wrapperInline)}>
        <div className={classNames(styles.actions, inline && styles.actionsInline)}>
          {showMemberships && (
            <div className={styles.action}>
              <Memberships
                items={memberships}
                allUsers={allUsers}
                permissionsSelectStep={BoardMembershipPermissionsSelectStep}
                // FAMILY-EXT: показываем кнопку добавления и для board-editor,
                // не только для project-manager — для семейного сценария
                // запрет на manager-only слишком строгий, сервер всё равно
                // верифицирует permissions при создании membership.
                canEdit={canEditMemberships || canEdit}
                onCreate={onMembershipCreate}
                onUpdate={onMembershipUpdate}
                onDelete={onMembershipDelete}
              />
            </div>
          )}
          {showFilters && (
            <div className={styles.action}>
              <Filters
                users={filterUsers}
                labels={filterLabels}
                filterText={filterText}
                allBoardMemberships={memberships}
                allLabels={labels}
                canEdit={canEdit}
                onUserAdd={onUserToFilterAdd}
                onUserRemove={onUserFromFilterRemove}
                onLabelAdd={onLabelToFilterAdd}
                onLabelRemove={onLabelFromFilterRemove}
                onLabelCreate={onLabelCreate}
                onLabelUpdate={onLabelUpdate}
                onLabelMove={onLabelMove}
                onLabelDelete={onLabelDelete}
                onTextFilterUpdate={onTextFilterUpdate}
                // FAMILY-EXT: feature 1
                showHiddenCards={showHiddenCards}
                onShowHiddenToggle={() => onShowHiddenToggle(boardId)}
                // FAMILY-EXT: фильтр "Только активные"
                showOnlyActive={showOnlyActive}
                onShowOnlyActiveToggle={() => onShowOnlyActiveToggle(boardId)}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

BoardActions.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  memberships: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  filterUsers: PropTypes.array.isRequired,
  filterLabels: PropTypes.array.isRequired,
  filterText: PropTypes.string.isRequired,
  allUsers: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  canEdit: PropTypes.bool.isRequired,
  canEditMemberships: PropTypes.bool.isRequired,
  onMembershipCreate: PropTypes.func.isRequired,
  onMembershipUpdate: PropTypes.func.isRequired,
  onMembershipDelete: PropTypes.func.isRequired,
  onUserToFilterAdd: PropTypes.func.isRequired,
  onUserFromFilterRemove: PropTypes.func.isRequired,
  onLabelToFilterAdd: PropTypes.func.isRequired,
  onLabelFromFilterRemove: PropTypes.func.isRequired,
  onLabelCreate: PropTypes.func.isRequired,
  onLabelUpdate: PropTypes.func.isRequired,
  onLabelMove: PropTypes.func.isRequired,
  onLabelDelete: PropTypes.func.isRequired,
  onTextFilterUpdate: PropTypes.func.isRequired,
  // FAMILY-EXT: feature 1
  showHiddenCards: PropTypes.string.isRequired,
  boardId: PropTypes.string,
  onShowHiddenToggle: PropTypes.func.isRequired,
  // FAMILY-EXT: фильтр "Только активные"
  showOnlyActive: PropTypes.bool,
  onShowOnlyActiveToggle: PropTypes.func,
  // FAMILY-EXT
  mode: PropTypes.oneOf(['all', 'memberships', 'filters']),
  inline: PropTypes.bool,
};

BoardActions.defaultProps = {
  // FAMILY-EXT: feature 1
  boardId: null,
  // FAMILY-EXT
  mode: 'all',
  inline: false,
  // FAMILY-EXT: фильтр "Только активные"
  showOnlyActive: false,
  onShowOnlyActiveToggle: () => {},
};

export default BoardActions;
